import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useWorkListHeader } from "hooks/Logistic/use-work-list-header";
import { useDisable } from "hooks/use-disable";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { FieldArrayWithId, FieldError, useFormContext } from "react-hook-form";
import { IDeliveryTrip } from "types/Logistic/deliveryTrip";
import MenuIcon from "@mui/icons-material/Menu";
import ControlledSelect from "components/Controller/ControlledSelect";
import { ISelectOption } from "types/global";
import ControlledTextField from "components/Controller/ControlledTextField";
import ControlledDateTimePicker from "components/Controller/ControlledDateTimePicker";
import { formatDateTime } from "utils/Date";
import ControlledNumberTextField from "components/Controller/ControlledNumberTextField";
import CloseIcon from "@mui/icons-material/Close";

type Props = {
  fields: FieldArrayWithId<IDeliveryTrip, "work_list", "id">[];
  dragEndHandler: (result: DropResult) => void;
  openDeleteItemConfirmationHandler: (index: number) => void;
  onTypeChange: () => void;
};

const workType: ISelectOption[] = [
  {
    id: 1,
    label: "รับ",
    value: "receive",
  },
  {
    id: 2,
    label: "ส่ง",
    value: "deliver",
  },
];

// 86d37db3t — one layout for every status. Cost columns are hidden per
// Hidden-not-deleted (values remain in DB on historical rows). Edit gates
// on ขึ้นสินค้าเสร็จ (end_date) and เลขกิโลเมตรถึงสาขา (arrived_kilometer)
// widen from `status !== "delivering"` → View-mode only.
const WorkListTable = ({
  fields,
  dragEndHandler,
  openDeleteItemConfirmationHandler,
  onTypeChange,
}: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<IDeliveryTrip>();
  const [disabled] = useDisable();
  const headers = useWorkListHeader();

  return (
    <TableContainer>
      <Table
        sx={{ minWidth: 650, overflow: "scroll" }}
        aria-label="simple table"
      >
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell
                align={header.align}
                key={index}
                sx={{
                  px: 1,
                  py: 1.5,
                  backgroundColor: "secondary.main",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    width: header.width,
                    justifyContent: header.align,
                  }}
                >
                  <Typography fontWeight={600}>{header.thaiLabel}</Typography>
                  {header.required && (
                    <Typography
                      fontSize={14}
                      color={"error.main"}
                      fontWeight={600}
                    >
                      *
                    </Typography>
                  )}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <DragDropContext onDragEnd={dragEndHandler}>
          <Droppable droppableId="droppable" isDropDisabled={disabled}>
            {(provided) => (
              <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                {fields.map((field, index) => (
                  <Draggable
                    key={field.unique_id}
                    draggableId={"work-" + field.unique_id}
                    index={index}
                    isDragDisabled={disabled}
                  >
                    {(provided) => (
                      <TableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          ...provided.draggableProps.style,
                          breakInside: "avoid",
                        }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                              width: headers[0]?.width,
                            }}
                          >
                            {!disabled && (
                              <MenuIcon
                                fontSize="small"
                                sx={{ color: "#BDBDBD" }}
                              />
                            )}
                            <Typography>{field.location_unique_id}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align={headers[1]?.align}>
                          <Box width={headers[1]?.width}>
                            <Typography>{field.location_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align={headers[2]?.align}>
                          <Box
                            width={headers[2]?.width}
                            mt={
                              Boolean(
                                errors?.work_list &&
                                  errors?.work_list[index]?.type
                              )
                                ? 3
                                : 0
                            }
                          >
                            {!disabled ? (
                              <ControlledSelect
                                control={control}
                                name={`work_list.${index}.type`}
                                options={workType}
                                error={Boolean(
                                  errors?.work_list &&
                                    errors?.work_list[index]?.type
                                )}
                                helperText={
                                  errors?.work_list &&
                                  (
                                    errors?.work_list[index]
                                      ?.type as FieldError
                                  )?.message
                                }
                                onChange={onTypeChange}
                              />
                            ) : (
                              <Typography>
                                {
                                  workType.find(
                                    (work) => work.value === field.type
                                  )?.label
                                }
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align={headers[3]?.align}>
                          <Box
                            width={headers[3]?.width}
                            mt={
                              Boolean(
                                errors?.work_list &&
                                  errors?.work_list[index]?.reference_code
                              )
                                ? 3
                                : 0
                            }
                          >
                            {!disabled ? (
                              <ControlledTextField
                                control={control}
                                name={`work_list.${index}.reference_code`}
                                error={Boolean(
                                  errors?.work_list &&
                                    errors.work_list[index]?.reference_code
                                )}
                                helperText={
                                  errors?.work_list &&
                                  errors.work_list[index]?.reference_code
                                    ?.message
                                }
                              />
                            ) : (
                              <Typography>
                                {field.reference_code || "-"}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align={headers[4]?.align}>
                          <Box width={headers[4]?.width}>
                            <Typography>
                              {field.distance
                                ? field.distance.toLocaleString()
                                : "-"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align={headers[5]?.align}>
                          <Box
                            width={headers[5]?.width}
                            mt={
                              Boolean(
                                errors?.work_list &&
                                  errors?.work_list[index]?.coordinator_name
                              )
                                ? 3
                                : 0
                            }
                          >
                            {!disabled ? (
                              <ControlledTextField
                                control={control}
                                name={`work_list.${index}.coordinator_name`}
                                error={Boolean(
                                  errors?.work_list &&
                                    errors.work_list[index]?.coordinator_name
                                )}
                                helperText={
                                  errors?.work_list &&
                                  errors.work_list[index]?.coordinator_name
                                    ?.message
                                }
                              />
                            ) : (
                              <Typography>{field.coordinator_name}</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align={headers[6]?.align}>
                          <Box
                            width={headers[6]?.width}
                            mt={
                              Boolean(
                                errors?.work_list &&
                                  errors?.work_list[index]?.coordinator_phone
                              )
                                ? 3
                                : 0
                            }
                          >
                            {!disabled ? (
                              <ControlledTextField
                                control={control}
                                name={`work_list.${index}.coordinator_phone`}
                                error={Boolean(
                                  errors?.work_list &&
                                    errors.work_list[index]?.coordinator_phone
                                )}
                                helperText={
                                  errors?.work_list &&
                                  errors.work_list[index]?.coordinator_phone
                                    ?.message
                                }
                              />
                            ) : (
                              <Typography>{field.coordinator_phone}</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align={headers[7]?.align}>
                          <Box
                            width={headers[7]?.width}
                            mt={
                              Boolean(
                                errors?.work_list &&
                                  errors?.work_list[index]?.special_request
                              )
                                ? 3
                                : 0
                            }
                          >
                            {!disabled ? (
                              <ControlledTextField
                                control={control}
                                name={`work_list.${index}.special_request`}
                                error={Boolean(
                                  errors?.work_list &&
                                    errors.work_list[index]?.special_request
                                )}
                                helperText={
                                  errors?.work_list &&
                                  errors.work_list[index]?.special_request
                                    ?.message
                                }
                              />
                            ) : (
                              <Typography>
                                {field.special_request || "-"}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align={headers[8]?.align}>
                          <Box
                            width={headers[8]?.width}
                            mt={
                              Boolean(
                                errors?.work_list &&
                                  errors?.work_list[index]?.customer_name
                              )
                                ? 3
                                : 0
                            }
                          >
                            {!disabled ? (
                              <ControlledTextField
                                control={control}
                                name={`work_list.${index}.customer_name`}
                                error={Boolean(
                                  errors?.work_list &&
                                    errors.work_list[index]?.customer_name
                                )}
                                helperText={
                                  errors?.work_list &&
                                  errors.work_list[index]?.customer_name
                                    ?.message
                                }
                              />
                            ) : (
                              <Typography>
                                {field.customer_name || "-"}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align={headers[9]?.align}>
                          <Box
                            width={headers[9]?.width}
                            mt={
                              Boolean(
                                errors?.work_list &&
                                  errors?.work_list[index]?.seal_no
                              )
                                ? 3
                                : 0
                            }
                          >
                            {!disabled ? (
                              <ControlledTextField
                                control={control}
                                name={`work_list.${index}.seal_no`}
                                error={Boolean(
                                  errors?.work_list &&
                                    errors.work_list[index]?.seal_no
                                )}
                                helperText={
                                  errors?.work_list &&
                                  errors.work_list[index]?.seal_no?.message
                                }
                              />
                            ) : (
                              <Typography>{field.seal_no || "-"}</Typography>
                            )}
                          </Box>
                        </TableCell>
                        {/* ขึ้นสินค้าเสร็จ — editable in every status; gate is
                            View-mode only per sheet. */}
                        <TableCell align={headers[10]?.align}>
                          <Box width={headers[10]?.width}>
                            {!disabled ? (
                              <ControlledDateTimePicker
                                control={control}
                                name={`work_list.${index}.end_date`}
                              />
                            ) : (
                              <Typography>
                                {field.end_date
                                  ? formatDateTime(field.end_date.toDate())
                                  : "-"}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        {/* เลขกิโลเมตรถึงสาขา — same widening. */}
                        <TableCell align={headers[11]?.align}>
                          <Box width={headers[11]?.width}>
                            {!disabled ? (
                              <ControlledNumberTextField
                                control={control}
                                name={`work_list.${index}.arrived_kilometer`}
                                error={Boolean(
                                  errors.work_list &&
                                    errors.work_list[index]?.arrived_kilometer
                                )}
                                decimalScale={0}
                              />
                            ) : (
                              <Typography>
                                {field.arrived_kilometer
                                  ? field.arrived_kilometer.toLocaleString()
                                  : "-"}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        {!disabled && (
                          <TableCell align="center">
                            <Box width={headers[12]?.width}>
                              <IconButton
                                aria-label="delete"
                                size="small"
                                sx={{ ml: 1, color: "rgba(0, 0, 0, 0.54)" }}
                                onClick={() =>
                                  openDeleteItemConfirmationHandler(index)
                                }
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
      </Table>
    </TableContainer>
  );
};

export default WorkListTable;
